<template>
  <!--
    A heat pump, drawn in the valve/pump idiom: one inline SVG on a 48-grid in
    `currentColor`, the casing semi-transparent so the refrigerant (or the brine,
    or the water) inside stays visible, and the moving part — the fan, the coil,
    the flame — in a colour of its own.

    Off, the machine is still and the fluid is invisible; running, the fan turns,
    the fluid runs through its circuit and takes the colour of what the pump is
    doing — warm when it heats, cold when it cools. Each kind says where the heat
    comes from: `air_water` the outdoor unit with its coil and plate exchanger,
    `ground` a pair of borehole brine loops, `water` a well, `air_air` an indoor
    louvre with its airflow, `exhaust` an extract duct and its exchanger cube,
    `dhw` a cylinder with its coil, `pool` a heater beside the pool's water, and
    `hybrid` the pump beside the boiler that backs it up.
  -->
  <svg
    class="hp-anim"
    :class="[`hp-anim--${kind}`, `hp-anim--${mode}`, { 'hp-anim--active': active }]"
    :style="{ '--hp-spin': spin }"
    viewBox="0 0 48 48"
    aria-hidden="true"
    focusable="false">
    <!-- ── Air-to-water: outdoor unit, its coil, and the plate exchanger ──── -->
    <g v-if="kind === 'air_water'">
      <path class="hp-anim__ground" d="M2,42.5 H46" />
      <rect class="hp-anim__casing" x="3.4" y="12.6" width="26" height="26.4" rx="3.2" />
      <circle class="hp-anim__rim" cx="16.4" cy="25.8" r="9.4" />
      <g class="hp-anim__blades" :style="{ transformOrigin: '16.4px 25.8px' }">
        <path d="M16.4,18.4 C20.4,20.6 19.6,24.4 16.4,25.8 C13.4,23.4 13.4,20.4 16.4,18.4 Z" />
        <path d="M23.4,29.8 C21.2,33.8 17.4,33 16.4,25.8 C19.4,27.4 21.8,28 23.4,29.8 Z" />
        <path d="M9.4,29.8 C11.6,33.8 15.4,33 16.4,25.8 C13.4,27.4 11,28 9.4,29.8 Z" />
      </g>
      <circle class="hp-anim__hub" cx="16.4" cy="25.8" r="1.7" />
      <path class="hp-anim__seam" d="M26.6,16.4 V35.2" />
      <path class="hp-anim__seam" d="M29.4,16.4 V35.2" />
      <path class="hp-anim__seam" d="M32.2,16.4 V35.2" />
      <rect class="hp-anim__body" x="34.6" y="16.8" width="11" height="18.4" rx="1.8" />
      <path class="hp-anim__seam" d="M36.8,20 H43.4 L36.8,24 H43.4 L36.8,28 H43.4 L36.8,32" />
      <g class="hp-anim__fluid">
        <path class="hp-anim__fluid-line" d="M27.4,18.6 H34.6" />
        <path class="hp-anim__current" d="M27.4,18.6 H34.6" />
        <path class="hp-anim__fluid-line" d="M27.4,33.4 H34.6" />
        <path class="hp-anim__current" d="M27.4,33.4 H34.6" />
      </g>
      <path class="hp-anim__pipe" d="M37.4,35.2 V46" />
      <path class="hp-anim__pipe" d="M42.8,35.2 V46" />
      <g class="hp-anim__fluid">
        <path class="hp-anim__fluid-line" d="M37.4,35.2 V46" />
        <path class="hp-anim__current" d="M37.4,35.2 V46" />
        <path class="hp-anim__fluid-line" d="M42.8,35.2 V46" />
        <path class="hp-anim__current" d="M42.8,35.2 V46" />
      </g>
      <path class="hp-anim__wall" d="M35.6,46 H44.6" />
      <g class="hp-anim__air">
        <path d="M8,10.4 Q12,6 18,8.2" />
        <path d="M14,7.6 Q18,4.2 23.4,6" />
      </g>
    </g>

    <!-- ── Ground-source: two borehole loops of brine under the unit ──────── -->
    <g v-else-if="kind === 'ground'">
      <path class="hp-anim__ground" d="M2,15 H46" />
      <path class="hp-anim__hatch" d="M6,18 L4,22" />
      <path class="hp-anim__hatch" d="M16,18 L14,22" />
      <path class="hp-anim__hatch" d="M26,18 L24,22" />
      <path class="hp-anim__hatch" d="M36,18 L34,22" />
      <path class="hp-anim__hatch" d="M44,18 L42,22" />
      <rect class="hp-anim__body" x="7.6" y="1.6" width="24.8" height="12.4" rx="2.4" />
      <circle class="hp-anim__rim" cx="15.8" cy="7.8" r="4.6" />
      <g class="hp-anim__blades" :style="{ transformOrigin: '15.8px 7.8px' }">
        <path d="M15.8,4.4 C18,5.4 17.6,7.2 15.8,7.8 C14,6.8 14,5.6 15.8,4.4 Z" />
        <path d="M19.2,9.8 C18,11.8 16.2,11.4 15.8,7.8 C17.4,8.6 18.6,8.8 19.2,9.8 Z" />
        <path d="M12.4,9.8 C13.6,11.8 15.4,11.4 15.8,7.8 C14.2,8.6 13,8.8 12.4,9.8 Z" />
      </g>
      <path class="hp-anim__seam" d="M23,4.6 V11" />
      <path class="hp-anim__seam" d="M26.2,4.6 V11" />
      <path class="hp-anim__seam" d="M29.4,4.6 V11" />
      <rect class="hp-anim__casing" x="9.6" y="14" width="20.8" height="2.6" rx="1.2" />
      <path class="hp-anim__pipe" d="M14,16.6 V40 C14,44 18.6,44 18.6,40 V16.6" />
      <path class="hp-anim__pipe" d="M23.4,16.6 V42 C23.4,46 28,46 28,42 V16.6" />
      <g class="hp-anim__fluid">
        <path class="hp-anim__fluid-line" d="M14,16.6 V40 C14,44 18.6,44 18.6,40 V16.6" />
        <path class="hp-anim__current" d="M14,16.6 V40 C14,44 18.6,44 18.6,40 V16.6" />
        <path class="hp-anim__fluid-line" d="M23.4,16.6 V42 C23.4,46 28,46 28,42 V16.6" />
        <path class="hp-anim__current" d="M23.4,16.6 V42 C23.4,46 28,46 28,42 V16.6" />
      </g>
    </g>

    <!-- ── Water-source: a well, its suction line and the exchanger ───────── -->
    <g v-else-if="kind === 'water'">
      <rect class="hp-anim__casing" x="4.4" y="15.4" width="14.4" height="29.2" rx="2.4" />
      <rect class="hp-anim__rest" x="5.6" y="31.6" width="12" height="11.8" />
      <path class="hp-anim__rest-surface" d="M5.6,31.6 H17.6" />
      <g class="hp-anim__fluid">
        <path class="hp-anim__current" d="M9,43.4 V34" />
        <path class="hp-anim__current" d="M14.4,43.4 V34" />
      </g>
      <rect class="hp-anim__body" x="21.6" y="7.4" width="22.8" height="14.4" rx="2" />
      <path class="hp-anim__seam" d="M24.6,10.6 H41.4 L24.6,14.6 H41.4 L24.6,18.6 H41.4" />
      <path class="hp-anim__pipe" d="M21.6,13.6 H11.6 V43" />
      <g class="hp-anim__fluid">
        <path class="hp-anim__fluid-line" d="M21.6,13.6 H11.6 V43" />
        <path class="hp-anim__current" d="M21.6,13.6 H11.6 V43" />
      </g>
      <path class="hp-anim__pipe" d="M38,21.8 V45.4" />
      <g class="hp-anim__fluid">
        <path class="hp-anim__fluid-line" d="M38,21.8 V45.4" />
        <path class="hp-anim__current" d="M38,21.8 V45.4" />
      </g>
      <path class="hp-anim__wall" d="M35.4,45.4 H41.4" />
      <path class="hp-anim__ground" d="M2,45.4 H46" />
    </g>

    <!-- ── Air-to-air: the indoor louvre and the air it throws ────────────── -->
    <g v-else-if="kind === 'air_air'">
      <rect class="hp-anim__casing" x="4.6" y="9.4" width="38.8" height="15.6" rx="4.4" />
      <rect class="hp-anim__body" x="8.6" y="17.4" width="30.8" height="4.6" rx="2.3" />
      <circle class="hp-anim__rim" cx="24" cy="13.4" r="2.6" />
      <g class="hp-anim__blades" :style="{ transformOrigin: '24px 13.4px' }">
        <path d="M24,11.2 C25.2,11.8 25,12.8 24,13.4 C22.8,12.8 22.8,12 24,11.2 Z" />
        <path d="M25.9,14.7 C25.2,15.9 24.1,15.6 24,13.4 C24.9,13.9 25.6,14.1 25.9,14.7 Z" />
        <path d="M22.1,14.7 C22.8,15.9 23.9,15.6 24,13.4 C23.1,13.9 22.4,14.1 22.1,14.7 Z" />
      </g>
      <g class="hp-anim__air">
        <path d="M13,26.4 Q17,32 15.4,41.4" />
        <path d="M24,26.4 Q26,33 24,43" />
        <path d="M35,26.4 Q31,32 32.6,41.4" />
        <path d="M18.4,26.4 Q20.4,31 19.4,37" />
        <path d="M29.6,26.4 Q27.6,31 28.6,37" />
      </g>
      <path class="hp-anim__seam" d="M8.6,11.8 H39.4" />
    </g>

    <!-- ── Exhaust-air: extract duct, exchanger cube, supply fan ──────────── -->
    <g v-else-if="kind === 'exhaust'">
      <rect class="hp-anim__casing" x="11.4" y="10.4" width="26.4" height="31.6" rx="3" />
      <rect class="hp-anim__casing" x="19.4" y="1.4" width="9.6" height="9.4" rx="1.4" />
      <path class="hp-anim__seam" d="M21.6,2.4 V9.4" />
      <path class="hp-anim__seam" d="M26.8,2.4 V9.4" />
      <rect class="hp-anim__casing" x="37.8" y="16.4" width="9" height="9.4" rx="1.4" />
      <path class="hp-anim__seam" d="M39.8,18.4 H44.8" />
      <path class="hp-anim__seam" d="M39.8,23.8 H44.8" />
      <path class="hp-anim__body" d="M24.6,17.6 L31.4,25.4 L24.6,33.2 L17.8,25.4 Z" />
      <path class="hp-anim__seam" d="M18.6,25.4 H30.6" />
      <circle class="hp-anim__rim" cx="31.2" cy="34.6" r="5.2" />
      <g class="hp-anim__blades" :style="{ transformOrigin: '31.2px 34.6px' }">
        <path d="M31.2,30.8 C33.6,32 33.2,34 31.2,34.6 C29.2,33.4 29.2,32.2 31.2,30.8 Z" />
        <path d="M35,36.8 C33.6,39 31.6,38.6 31.2,34.6 C33,35.4 34.2,35.6 35,36.8 Z" />
        <path d="M27.4,36.8 C28.8,39 30.8,38.6 31.2,34.6 C29.4,35.4 28.2,35.6 27.4,36.8 Z" />
      </g>
      <circle class="hp-anim__hub" cx="31.2" cy="34.6" r="1.1" />
      <g class="hp-anim__fluid">
        <path class="hp-anim__current" d="M24.2,3.4 V9.4" />
        <path class="hp-anim__current" d="M24.2,9.4 V17.6" />
      </g>
      <g class="hp-anim__air">
        <path d="M38.2,21 Q42.6,21 44.6,18.4" />
        <path d="M42,26.4 Q45.2,26.4 46.4,24.6" />
      </g>
    </g>

    <!-- ── Hot water: the cylinder, its coil and the pump on its lid ──────── -->
    <g v-else-if="kind === 'dhw'">
      <rect class="hp-anim__body" x="15.4" y="0.8" width="19.2" height="7.4" rx="2.2" />
      <circle class="hp-anim__rim" cx="25" cy="4.4" r="2.1" />
      <g class="hp-anim__blades" :style="{ transformOrigin: '25px 4.4px' }">
        <path d="M25,2.6 C26,3.1 25.8,3.9 25,4.4 C24,3.9 24,3.3 25,2.6 Z" />
        <path d="M26.5,5.4 C26,6.4 25.1,6.2 25,4.4 C25.7,4.8 26.3,5 26.5,5.4 Z" />
        <path d="M23.5,5.4 C24,6.4 24.9,6.2 25,4.4 C24.3,4.8 23.7,5 23.5,5.4 Z" />
      </g>
      <rect class="hp-anim__casing" x="15.4" y="9.6" width="19.2" height="37.4" rx="9.6" />
      <rect class="hp-anim__rest" x="16.8" y="21.4" width="16.4" height="24.2" rx="8" />
      <path class="hp-anim__rest-surface" d="M16.8,22.6 Q25,20.6 33.2,22.6" />
      <g class="hp-anim__fluid">
        <circle class="hp-anim__bubble" cx="21.4" cy="38.4" r="1" />
        <circle class="hp-anim__bubble" cx="27.6" cy="35.4" r="0.85" />
        <circle class="hp-anim__bubble" cx="24.2" cy="31.6" r="0.75" />
      </g>
      <path class="hp-anim__coil" d="M18.6,33.4 Q25,29.6 31.4,33.4" />
      <path class="hp-anim__coil" d="M18.6,38.6 Q25,34.8 31.4,38.6" />
      <path class="hp-anim__pipe" d="M25,17 V9.6" />
      <g class="hp-anim__fluid">
        <path class="hp-anim__fluid-line" d="M25,17.4 V9.6" />
        <path class="hp-anim__current" d="M25,17.4 V9.6" />
      </g>
    </g>

    <!-- ── Pool: the heater beside the water it warms ─────────────────────── -->
    <g v-else-if="kind === 'pool'">
      <rect class="hp-anim__body" x="5.6" y="8.4" width="23.4" height="18.4" rx="2.2" />
      <circle class="hp-anim__rim" cx="13.8" cy="17.6" r="6.4" />
      <g class="hp-anim__blades" :style="{ transformOrigin: '13.8px 17.6px' }">
        <path d="M13.8,12.4 C16.6,13.8 16.2,16.4 13.8,17.6 C11.4,16.2 11.4,14.4 13.8,12.4 Z" />
        <path d="M18.4,21.4 C16.8,24 14.4,23.4 13.8,17.6 C16,18.6 17.8,19 18.4,21.4 Z" />
        <path d="M9.2,21.4 C10.8,24 13.2,23.4 13.8,17.6 C11.6,18.6 9.8,19 9.2,21.4 Z" />
      </g>
      <circle class="hp-anim__hub" cx="13.8" cy="17.6" r="1.3" />
      <path class="hp-anim__seam" d="M23.4,11.4 V23.8" />
      <path class="hp-anim__seam" d="M26.2,11.4 V23.8" />
      <path class="hp-anim__pipe" d="M29,17.6 H38.4 V32" />
      <g class="hp-anim__fluid">
        <path class="hp-anim__fluid-line" d="M29,17.6 H38.4 V32" />
        <path class="hp-anim__current" d="M29,17.6 H38.4 V32" />
      </g>
      <rect class="hp-anim__casing" x="2.4" y="32.6" width="43.2" height="13" rx="2.2" />
      <rect class="hp-anim__rest" x="3.6" y="34.4" width="40.8" height="10" rx="1.6" />
      <path
        class="hp-anim__rest-surface"
        d="M3.6,35 Q9,33.4 14.4,35 Q19.8,36.6 25.2,35 Q30.6,33.4 36,35 Q40.2,36.2 44.4,35" />
    </g>

    <!-- ── Hybrid: the pump and the boiler that backs it up ───────────────── -->
    <g v-else>
      <rect class="hp-anim__casing" x="2.6" y="12.4" width="21.2" height="28.4" rx="3" />
      <circle class="hp-anim__rim" cx="13.2" cy="26.6" r="7.6" />
      <g class="hp-anim__blades" :style="{ transformOrigin: '13.2px 26.6px' }">
        <path d="M13.2,20.4 C16.4,22 16,25 13.2,26.6 C10.4,25 10.4,22.6 13.2,20.4 Z" />
        <path d="M18.8,31.4 C17,34.6 13.8,34 13.2,26.6 C15.8,27.8 18,28.4 18.8,31.4 Z" />
        <path d="M7.6,31.4 C9.4,34.6 12.6,34 13.2,26.6 C10.6,27.8 8.4,28.4 7.6,31.4 Z" />
      </g>
      <circle class="hp-anim__hub" cx="13.2" cy="26.6" r="1.4" />
      <path class="hp-anim__seam" d="M19,15.4 V37.8" />
      <rect class="hp-anim__casing" x="27.4" y="16.4" width="17.2" height="24.4" rx="8.2" />
      <path
        class="hp-anim__flame"
        d="M36,35.6 C32.8,33.4 33.6,29.8 35.6,27.4 C35.8,29.6 37,30.6 38,30.4 C38,28.4 38.6,26.6 39.4,25.6 C41,29.6 40.2,33.6 36,35.6 Z" />
      <path class="hp-anim__pipe" d="M23.8,30.6 H27.4" />
      <g class="hp-anim__fluid">
        <path class="hp-anim__fluid-line" d="M23.8,30.6 H27.4" />
        <path class="hp-anim__current" d="M23.8,30.6 H27.4" />
        <path class="hp-anim__fluid-line" d="M27.4,38.6 H23.8" />
        <path class="hp-anim__current" d="M27.4,38.6 H23.8" />
      </g>
      <path class="hp-anim__ground" d="M2,40.8 H46" />
    </g>
  </svg>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { HeatPumpKind } from '../deviceWidgetTypes';

const props = withDefaults(
  defineProps<{
    kind?: HeatPumpKind;
    /** The compressor is running — the only thing the fluid and the motion key off. */
    active?: boolean;
    /** What the pump is doing, which colours the fluid: warm heating, cold cooling. */
    mode?: 'heat' | 'cool' | 'idle';
  }>(),
  { kind: 'air_water', active: false, mode: 'idle' },
);

/** The fan's turn — a compressor running is a fan turning, never a blur. */
const spin = computed(() => (props.active ? '2.4s' : '0s'));
</script>

<style scoped>
.hp-anim {
  display: block;
  width: 100%;
  height: 100%;
  /* The fluid takes the colour of what the machine is doing. */
  --hp-fluid: #38bdf8;
  --hp-fluid-soft: #7dd3fc;
}

.hp-anim--heat {
  --hp-fluid: #fb923c;
  --hp-fluid-soft: #fdba74;
}

.hp-anim--cool {
  --hp-fluid: #38bdf8;
  --hp-fluid-soft: #7dd3fc;
}

/* ── The casing: metal, semi-transparent so the fluid inside shows through ── */
.hp-anim__casing {
  fill: color-mix(in srgb, currentColor 26%, transparent);
  stroke: color-mix(in srgb, currentColor 78%, rgb(0 0 0));
  stroke-width: 1.2;
}

.hp-anim__body {
  fill: color-mix(in srgb, currentColor 52%, transparent);
  stroke: color-mix(in srgb, currentColor 80%, rgb(0 0 0));
  stroke-width: 1.1;
}

.hp-anim__pipe {
  fill: none;
  stroke: color-mix(in srgb, currentColor 32%, transparent);
  stroke-width: 5.4;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.hp-anim__wall {
  fill: none;
  stroke: color-mix(in srgb, currentColor 74%, transparent);
  stroke-width: 1.3;
  stroke-linecap: round;
}

.hp-anim__ground {
  fill: none;
  stroke: color-mix(in srgb, currentColor 60%, transparent);
  stroke-width: 1.4;
  stroke-linecap: round;
}

.hp-anim__hatch {
  fill: none;
  stroke: color-mix(in srgb, currentColor 34%, transparent);
  stroke-width: 1;
  stroke-linecap: round;
}

.hp-anim__rim {
  fill: none;
  stroke: color-mix(in srgb, currentColor 62%, transparent);
  stroke-width: 1.1;
}

.hp-anim__seam {
  fill: none;
  stroke: color-mix(in srgb, currentColor 66%, transparent);
  stroke-width: 0.9;
  stroke-linecap: round;
  stroke-linejoin: round;
}

/* ── The fluid: brine, water or refrigerant, in the colour of the job ────── */
.hp-anim__fluid {
  opacity: 0;
  transition: opacity 0.4s ease;
}

.hp-anim--active .hp-anim__fluid {
  opacity: 1;
}

.hp-anim__fluid-body {
  fill: var(--hp-fluid);
  fill-opacity: 0.5;
}

.hp-anim__surface {
  fill: none;
  stroke: var(--hp-fluid-soft);
  stroke-width: 1.2;
  stroke-linecap: round;
}

.hp-anim__fluid-line {
  fill: none;
  stroke: var(--hp-fluid);
  stroke-width: 3.4;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.hp-anim__current {
  fill: none;
  stroke: #f8fafc;
  stroke-opacity: 0.85;
  stroke-width: 1.2;
  stroke-linecap: round;
  stroke-dasharray: 2.6 5.4;
}

.hp-anim__bubble {
  fill: var(--hp-fluid-soft);
}

/* Standing water — a well and a pool hold theirs whether the machine runs or not. */
.hp-anim__rest {
  fill: var(--hp-fluid);
  fill-opacity: 0.26;
  transition: fill-opacity 0.4s ease;
}

.hp-anim--active .hp-anim__rest {
  fill-opacity: 0.5;
}

.hp-anim__rest-surface {
  fill: none;
  stroke: var(--hp-fluid-soft);
  stroke-opacity: 0.55;
  stroke-width: 1.1;
  stroke-linecap: round;
}

/* ── The moving parts, in a colour of their own ─────────────────────────── */
.hp-anim__blades {
  fill: #f59e0b;
  fill-opacity: 0.9;
  stroke: #b45309;
  stroke-width: 0.7;
  stroke-linejoin: round;
}

.hp-anim__hub {
  fill: #f59e0b;
}

.hp-anim__coil {
  fill: none;
  stroke: #f59e0b;
  stroke-width: 1.6;
  stroke-linecap: round;
}

/* The boiler's flame is cold until the burner lights — which is only when the
   pump is heating. */
.hp-anim__flame {
  fill: color-mix(in srgb, currentColor 30%, transparent);
  stroke: color-mix(in srgb, currentColor 55%, transparent);
  stroke-width: 0.8;
  transition:
    fill 0.3s ease,
    stroke 0.3s ease;
}

.hp-anim--active.hp-anim--heat .hp-anim__flame {
  fill: #f97316;
  stroke: #c2410c;
}

.hp-anim__air {
  fill: none;
  stroke: var(--hp-fluid-soft);
  stroke-width: 1.3;
  stroke-linecap: round;
  stroke-dasharray: 2.4 3.2;
  opacity: 0.55;
}

/* ── Running: the fan turns, the fluid runs, each kind's own gesture ─────── */
.hp-anim--active .hp-anim__blades {
  animation: hp-anim-spin var(--hp-spin, 2.4s) linear infinite;
}

.hp-anim--active .hp-anim__current {
  animation: hp-anim-current 1.3s linear infinite;
}

.hp-anim--active .hp-anim__bubble {
  animation: hp-anim-bubble 2s ease-in infinite;
}

.hp-anim--active .hp-anim__bubble:nth-child(2) {
  animation-delay: 0.6s;
}

.hp-anim--active .hp-anim__bubble:nth-child(3) {
  animation-delay: 1.15s;
}

.hp-anim--active .hp-anim__coil {
  animation: hp-anim-coil 1.8s ease-in-out infinite;
}

.hp-anim--active .hp-anim__flame {
  animation: hp-anim-flame 0.9s ease-in-out infinite;
  transform-box: fill-box;
  transform-origin: 50% 90%;
}

.hp-anim--cool .hp-anim__flame {
  animation: none;
}

.hp-anim--active .hp-anim__air {
  animation: hp-anim-air 1.6s linear infinite;
}

@keyframes hp-anim-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes hp-anim-current {
  to {
    stroke-dashoffset: -8;
  }
}

@keyframes hp-anim-bubble {
  0% {
    opacity: 0;
    transform: translateY(0);
  }
  25% {
    opacity: 0.9;
  }
  100% {
    opacity: 0;
    transform: translateY(-8px);
  }
}

@keyframes hp-anim-coil {
  0%,
  100% {
    stroke-opacity: 0.55;
  }
  50% {
    stroke-opacity: 1;
  }
}

@keyframes hp-anim-flame {
  0%,
  100% {
    transform: scaleY(0.94);
  }
  50% {
    transform: scaleY(1.05);
  }
}

@keyframes hp-anim-air {
  to {
    stroke-dashoffset: -11;
  }
}

/* Reduced motion: the drawing stays, the movement stops. */
@media (prefers-reduced-motion: reduce) {
  .hp-anim--active .hp-anim__blades,
  .hp-anim--active .hp-anim__current,
  .hp-anim--active .hp-anim__bubble,
  .hp-anim--active .hp-anim__coil,
  .hp-anim--active .hp-anim__flame,
  .hp-anim--active .hp-anim__air {
    animation: none !important;
  }
}
</style>
